<template>
  <a-drawer
    :visible="visible"
    :width="drawerWidth"
    title="주최자 인증서 수신자 마이그레이션"
    placement="right"
    @close="handleClose"
    class="organizer-cert-migration-drawer"
  >
    <a-steps :current="currentStep" size="small" style="margin-bottom: 24px;">
      <a-step title="기간 선택" />
      <a-step title="미리보기" />
      <a-step title="실행" />
    </a-steps>

    <!-- Step 1: 기간 선택 -->
    <div v-show="currentStep === 0" class="step-content">
      <a-alert
        message="주최자의 인증서 수신자(NFT 보유자)를 이벤트 참여자로 추가합니다."
        description="이벤트의 주최자 정보를 기반으로 해당 기간 동안 인증서를 받은 고객을 자동으로 추출합니다."
        type="info"
        show-icon
        style="margin-bottom: 16px;"
      />

      <a-form layout="vertical">
        <a-form-item label="타겟 이벤트">
          <a-input :value="eventTitle" disabled />
          <div class="event-id-hint">ID: {{ targetEventId }}</div>
        </a-form-item>

        <a-form-item label="기간 선택">
          <a-radio-group v-model:value="dateRangePreset" @change="handlePresetChange">
            <a-space direction="vertical">
              <a-radio value="1_MONTH">최근 1개월</a-radio>
              <a-radio value="2_MONTHS">최근 2개월</a-radio>
              <a-radio value="3_MONTHS">최근 3개월</a-radio>
              <a-radio value="6_MONTHS">최근 6개월</a-radio>
              <a-radio value="CUSTOM">직접 입력</a-radio>
            </a-space>
          </a-radio-group>
        </a-form-item>

        <a-form-item v-if="dateRangePreset === 'CUSTOM'" label="커스텀 기간">
          <a-range-picker
            v-model:value="customDateRange"
            :placeholder="['시작일', '종료일']"
            style="width: 100%;"
          />
        </a-form-item>
      </a-form>

      <div class="drawer-footer">
        <a-button @click="handleClose">취소</a-button>
        <a-button
          type="primary"
          :loading="extractLoading"
          :disabled="!canExtract"
          @click="executeExtract"
          class="yellow-button"
        >
          미리보기
        </a-button>
      </div>
    </div>

    <!-- Step 2: 미리보기 -->
    <div v-show="currentStep === 1" class="step-content">
      <div v-if="extractResult" class="extract-result">
        <a-descriptions :column="1" bordered size="small" style="margin-bottom: 16px;">
          <a-descriptions-item label="주최자">
            {{ extractResult.data?.organizerName || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="기간">
            {{ extractResult.data?.dateRange?.start }} ~ {{ extractResult.data?.dateRange?.end }}
          </a-descriptions-item>
        </a-descriptions>

        <a-row :gutter="16" class="result-summary">
          <a-col :span="6">
            <a-statistic
              title="총 NFT"
              :value="extractResult.data?.totalNfts || 0"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic
              title="기존 참여자"
              :value="extractResult.data?.existingAttendees || 0"
              :value-style="{ color: '#999' }"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic
              title="신규 추가 예정"
              :value="extractResult.data?.toCreateCount || 0"
              :value-style="{ color: '#52c41a' }"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic
              title="중복/무효"
              :value="(extractResult.data?.duplicateCount || 0) + (extractResult.data?.invalidCount || 0)"
              :value-style="{ color: '#faad14' }"
            />
          </a-col>
        </a-row>

        <!-- 미리보기 테이블 -->
        <div v-if="extractResult.data?.preview?.length > 0" class="preview-section">
          <a-divider>미리보기 (최대 100건)</a-divider>
          <a-table
            :dataSource="extractResult.data.preview"
            :columns="previewColumns"
            size="small"
            :pagination="{ pageSize: 10 }"
            :scroll="{ y: 200 }"
            rowKey="customerMobile"
          />
        </div>
      </div>

      <div class="drawer-footer">
        <a-button @click="currentStep = 0">이전</a-button>
        <a-button
          type="primary"
          :disabled="!extractResult?.data?.toCreateCount"
          @click="currentStep = 2"
          class="yellow-button"
        >
          다음
        </a-button>
      </div>
    </div>

    <!-- Step 3: 실행 -->
    <div v-show="currentStep === 2" class="step-content">
      <a-alert
        v-if="!executeResult"
        message="마이그레이션 실행"
        :description="`${extractResult?.data?.toCreateCount || 0}명의 인증서 수신자를 참여자로 추가합니다. 시뮬레이션으로 먼저 테스트해보세요.`"
        type="warning"
        show-icon
        style="margin-bottom: 16px;"
      />

      <a-form v-if="!executeResult" layout="vertical">
        <a-form-item label="실행 모드">
          <a-radio-group v-model:value="dryRun">
            <a-radio :value="true">
              <span>시뮬레이션 (Dry Run)</span>
              <a-tooltip title="실제 변경 없이 결과만 미리보기">
                <i class="fa fa-question-circle help-icon"></i>
              </a-tooltip>
            </a-radio>
            <a-radio :value="false">실제 실행</a-radio>
          </a-radio-group>
        </a-form-item>
      </a-form>

      <!-- 실행 결과 -->
      <div v-if="executeResult" class="execute-result">
        <a-result
          :status="executeResult.success ? 'success' : 'error'"
          :title="executeResult.success ? '마이그레이션 완료' : '마이그레이션 실패'"
          :sub-title="executeResult.message"
        />

        <a-row :gutter="16" class="result-summary" v-if="executeResult.data">
          <a-col :span="6">
            <a-statistic
              title="처리됨"
              :value="executeResult.data.totalProcessed || 0"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic
              title="생성"
              :value="executeResult.data.created || 0"
              :value-style="{ color: '#52c41a' }"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic
              title="업데이트"
              :value="executeResult.data.updated || 0"
              :value-style="{ color: '#1890ff' }"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic
              title="실패"
              :value="executeResult.data.failed || 0"
              :value-style="{ color: '#ff4d4f' }"
            />
          </a-col>
        </a-row>

        <a-tag v-if="executeResult.data?.dryRun" color="orange" style="margin-top: 8px;">
          시뮬레이션 모드 - 실제 데이터 변경 없음
        </a-tag>

        <!-- 실패 목록 -->
        <div v-if="executeResult.data?.errors?.length > 0" class="error-list">
          <a-collapse>
            <a-collapse-panel header="실패 목록 상세보기">
              <a-table
                :dataSource="executeResult.data.errors"
                :columns="errorColumns"
                size="small"
                :pagination="{ pageSize: 10 }"
                :scroll="{ y: 200 }"
              />
            </a-collapse-panel>
          </a-collapse>
        </div>
      </div>

      <div class="drawer-footer">
        <a-button @click="executeResult ? resetAll() : currentStep = 1">
          {{ executeResult ? '처음으로' : '이전' }}
        </a-button>
        <a-button
          v-if="!executeResult"
          type="primary"
          :loading="executeLoading"
          @click="executeMigration"
          class="yellow-button"
        >
          {{ dryRun ? '시뮬레이션 실행' : '마이그레이션 실행' }}
        </a-button>
        <a-button
          v-else
          type="primary"
          @click="handleClose"
          class="yellow-button"
        >
          완료
        </a-button>
      </div>
    </div>
  </a-drawer>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { message } from 'ant-design-vue';

export default {
  name: 'OrganizerCertificateMigrationDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    targetEventId: {
      type: String,
      required: true
    },
    eventTitle: {
      type: String,
      default: ''
    },
    apiInstance: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'migration-complete'],
  setup(props, { emit }) {
    // Drawer width (responsive)
    const drawerWidth = computed(() => {
      if (typeof window !== 'undefined' && window.innerWidth <= 767) {
        return '100%';
      }
      return 600;
    });

    // Step state
    const currentStep = ref(0);

    // Form states
    const dateRangePreset = ref('1_MONTH');
    const customDateRange = ref(null);
    const dryRun = ref(true);

    // Loading states
    const extractLoading = ref(false);
    const executeLoading = ref(false);

    // Result states
    const extractResult = ref(null);
    const executeResult = ref(null);

    // Computed
    const canExtract = computed(() => {
      if (!props.targetEventId) return false;
      if (dateRangePreset.value === 'CUSTOM') {
        return customDateRange.value && customDateRange.value.length === 2;
      }
      return true;
    });

    // Preview table columns
    const previewColumns = [
      { title: '이름', dataIndex: 'customerName', key: 'customerName', width: 120 },
      { title: '전화번호', dataIndex: 'customerMobile', key: 'customerMobile', width: 140 }
    ];

    // Error table columns
    const errorColumns = [
      { title: '전화번호', dataIndex: 'mobile', key: 'mobile', width: 140 },
      { title: '사유', dataIndex: 'error', key: 'error' }
    ];

    // Reset states when drawer closes
    watch(() => props.visible, (newVal) => {
      if (!newVal) {
        resetAll();
      }
    });

    // Handle preset change
    const handlePresetChange = () => {
      if (dateRangePreset.value !== 'CUSTOM') {
        customDateRange.value = null;
      }
    };

    // Execute extract (preview)
    const executeExtract = async () => {
      if (!props.targetEventId) {
        message.warning('타겟 이벤트가 선택되지 않았습니다.');
        return;
      }

      extractLoading.value = true;
      extractResult.value = null;

      const params = {
        eventId: props.targetEventId,
        dateRangePreset: dateRangePreset.value,
        limit: 100
      };

      if (dateRangePreset.value === 'CUSTOM' && customDateRange.value) {
        params.customStartDate = customDateRange.value[0].format('YYYY-MM-DD');
        params.customEndDate = customDateRange.value[1].format('YYYY-MM-DD');
      }

      try {
        await props.apiInstance.extractOrganizerCertificateRecipients(
          params,
          (response) => {
            extractResult.value = response.data || response;
            currentStep.value = 1;
            message.success('미리보기가 완료되었습니다.');
          },
          (error) => {
            message.error(`미리보기 실패: ${error.response?.data?.message || error.message}`);
          }
        );
      } finally {
        extractLoading.value = false;
      }
    };

    // Execute migration
    const executeMigration = async () => {
      if (!props.targetEventId) {
        message.warning('타겟 이벤트가 선택되지 않았습니다.');
        return;
      }

      executeLoading.value = true;
      executeResult.value = null;

      const params = {
        eventId: props.targetEventId,
        dateRangePreset: dateRangePreset.value,
        dryRun: dryRun.value
      };

      if (dateRangePreset.value === 'CUSTOM' && customDateRange.value) {
        params.customStartDate = customDateRange.value[0].format('YYYY-MM-DD');
        params.customEndDate = customDateRange.value[1].format('YYYY-MM-DD');
      }

      try {
        await props.apiInstance.executeOrganizerCertificateMigration(
          params,
          (response) => {
            executeResult.value = response.data || response;
            const action = dryRun.value ? '시뮬레이션' : '마이그레이션';
            message.success(`${action}이 완료되었습니다.`);

            if (!dryRun.value) {
              emit('migration-complete');
            }
          },
          (error) => {
            executeResult.value = {
              success: false,
              message: error.response?.data?.message || error.message
            };
            message.error(`마이그레이션 실패: ${error.response?.data?.message || error.message}`);
          }
        );
      } finally {
        executeLoading.value = false;
      }
    };

    // Reset all states
    const resetAll = () => {
      currentStep.value = 0;
      dateRangePreset.value = '1_MONTH';
      customDateRange.value = null;
      dryRun.value = true;
      extractResult.value = null;
      executeResult.value = null;
    };

    // Handle close
    const handleClose = () => {
      emit('close');
    };

    return {
      drawerWidth,
      currentStep,
      dateRangePreset,
      customDateRange,
      dryRun,
      extractLoading,
      executeLoading,
      extractResult,
      executeResult,
      canExtract,
      previewColumns,
      errorColumns,
      handlePresetChange,
      executeExtract,
      executeMigration,
      resetAll,
      handleClose
    };
  }
};
</script>

<style lang="scss" scoped>
.organizer-cert-migration-drawer {
  .step-content {
    padding-bottom: 60px;
  }

  .event-id-hint {
    font-size: 12px;
    color: #999;
    margin-top: 4px;
  }

  .help-icon {
    margin-left: 4px;
    color: #999;
    cursor: help;
  }

  .result-summary {
    margin-bottom: 16px;

    .ant-statistic {
      text-align: center;
    }
  }

  .preview-section {
    margin-top: 16px;
  }

  .execute-result {
    margin-top: 16px;
  }

  .error-list {
    margin-top: 16px;
  }

  .drawer-footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px 24px;
    background: #fff;
    border-top: 1px solid #e8e8e8;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  .yellow-button {
    background-color: #fadb14 !important;
    border-color: #fadb14 !important;
    color: #000 !important;

    &:hover {
      background-color: #ffc53d !important;
      border-color: #ffc53d !important;
    }

    &:disabled {
      background-color: #f5f5f5 !important;
      border-color: #d9d9d9 !important;
      color: rgba(0, 0, 0, 0.25) !important;
    }
  }
}
</style>
